import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextLazy';
import { getSupabase } from '../../lib/supabase-lazy';
import { VisualJourneyCanvas } from '../../components/project/VisualJourneyCanvas';
import TaskSidePanel from '../../components/project/TaskSidePanel';
import TaskModal from '../../components/project/TaskModal';
import ProjectSettingsModal from '../../components/project/ProjectSettingsModal';
import { TaskListView } from '../../components/project/TaskListView';
import {
  ArrowLeftIcon, PlusIcon, Cog6ToothIcon, DocumentArrowDownIcon,
  DocumentDuplicateIcon, ArchiveBoxIcon, ViewColumnsIcon,
  TableCellsIcon, UsersIcon
} from '@heroicons/react/24/outline';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { colors } from '../../lib/colors';

interface Project {
  id: string;
  name: string;
  description: string | null;
  status: 'active' | 'completed' | 'on_hold' | 'cancelled';
  client_id: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  client?: {
    name: string;
  };
}

import { ProjectTask } from '../../types/project';

interface ActiveUser {
  id: string;
  full_name: string;
  last_activity: string;
}

export const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'vertical' | 'flowchart' | 'timeline' | 'kanban'>('vertical');
  const [displayMode, setDisplayMode] = useState<'visual' | 'list'>('visual');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<ProjectTask | null>(null);
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [supabase, setSupabase] = useState<any>(null);

  useEffect(() => {
    getSupabase().then(setSupabase);
  }, []);

  useEffect(() => {
    if (projectId && supabase) {
      fetchProjectDetails();
      fetchProjectTasks();
      fetchActiveUsers();
      const unsubscribe = subscribeToUpdates();

      // Set up auto-refresh for active users
      refreshIntervalRef.current = setInterval(() => {
        fetchActiveUsers();
      }, 30000); // Refresh every 30 seconds

      return () => {
        unsubscribe();
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [projectId, supabase]);

  const fetchProjectDetails = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          client:parties(name)
        `)
        .eq('id', projectId)
        .single();

      if (error) throw error;
      setProject(data);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching project:', error);
      }
    }
  };

  const fetchProjectTasks = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('project_tasks')
        .select(`
          *,
          assignee:platform_users!assigned_to(
            id,
            full_name
          )
        `);

      if (error) throw error;
      // Map title to name for compatibility
      const mappedTasks = (data || []).map((task: any) => ({
        ...task,
        name: task.title,
        order_index: task.position_y
      }));
      setTasks(mappedTasks);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching tasks:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(tasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update positions
    const updatedItems = items.map((task: ProjectTask, index: number) => ({
      ...task,
      position_x: index * 100, // Simplified positioning
      position_y: index * 100,
    }));

    setTasks(updatedItems);
  };

  const fetchActiveUsers = async () => {
    if (!supabase) return;
    try {
      // Get users who have been active on this project in the last 5 minutes
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('project_activity_presence')
        .select(`
          user_id,
          last_activity,
          user:platform_users(
            id,
            full_name
          )
        `)
        .eq('project_id', projectId)
        .gte('last_activity', fiveMinutesAgo);

      if (error) throw error;

      const users = data?.map((item: any) => ({
        id: item.user?.id,
        full_name: item.user?.full_name,
        last_activity: item.last_activity
      })).filter((u: any) => u.id !== userProfile?.id) || [];

      setActiveUsers(users);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching active users:', error);
      }
    }
  };

  const subscribeToUpdates = () => {
    if (!supabase) return () => { };
    const channel = supabase
      .channel(`project:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_tasks',
          filter: `project_id=eq.${projectId}`
        },
        (payload: any) => {
          if (import.meta.env.DEV) {
            console.log('Task update:', payload);
          }
          fetchProjectTasks();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  };

  const handleTaskSelect = (taskId: string) => {
    setSelectedTaskId(taskId);
  };

  const handleTaskStatusChange = async (taskId: string, newStatus: string) => {
    if (!supabase) return;
    try {
      const { error } = await supabase
        .from('project_tasks')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId);

      if (error) throw error;

      // Also log activity
      await supabase.from('task_activities').insert({
        task_id: taskId,
        activity_type: 'status_change',
        description: `Status changed to ${newStatus}`,
        created_by: userProfile?.id,
        metadata: { old_status: tasks.find(t => t.id === taskId)?.status, new_status: newStatus }
      });

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating task status:', error);
      }
    }
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const handleEditTask = (task: ProjectTask) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleSaveTask = async (taskData: Partial<ProjectTask>) => {
    if (!supabase || !projectId) return;

    try {
      if (editingTask) {
        // Update existing task
        const { error } = await supabase
          .from('project_tasks')
          .update({
            title: taskData.name || editingTask.title,
            description: taskData.description,
            task_type: taskData.task_type || editingTask.task_type,
            status: taskData.status || editingTask.status,
            assigned_to: taskData.assigned_to,
            due_date: taskData.due_date,
            depends_on: taskData.depends_on,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingTask.id);

        if (error) throw error;
      } else {
        // Create new task
        const maxPosition = Math.max(...tasks.map(t => t.position_y || 0), -1);
        const { error } = await supabase
          .from('project_tasks')
          .insert({
            project_id: projectId,
            title: taskData.name || '',
            description: taskData.description,
            task_type: taskData.task_type || 'task',
            status: taskData.status || 'pending',
            assigned_to: taskData.assigned_to,
            due_date: taskData.due_date,
            depends_on: taskData.depends_on,
            position_y: maxPosition + 1,
            position_x: 0
          });

        if (error) throw error;
      }

      setShowTaskModal(false);
      fetchProjectTasks();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error saving task:', error);
      }
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!supabase || !window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const { error } = await supabase
        .from('project_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      fetchProjectTasks();
      if (selectedTaskId === taskId) {
        setSelectedTaskId(null);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const handleReorderTasks = async (reorderedTasks: ProjectTask[]) => {
    if (!supabase) return;

    try {
      // Update order_index for all tasks
      const updates = reorderedTasks.map((task, index) => ({
        id: task.id,
        order_index: index
      }));

      for (const update of updates) {
        await supabase
          .from('project_tasks')
          .update({ order_index: update.order_index })
          .eq('id', update.id);
      }

      setTasks(reorderedTasks);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error reordering tasks:', error);
      }
    }
  };

  const handleBulkStatusUpdate = async (taskIds: string[], newStatus: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('project_tasks')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .in('id', taskIds);

      if (error) throw error;

      fetchProjectTasks();
      setSelectedTasks([]);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating tasks:', error);
      }
    }
  };

  const handleBulkDelete = async (taskIds: string[]) => {
    if (!supabase || !window.confirm(`Are you sure you want to delete ${taskIds.length} tasks?`)) return;

    try {
      const { error } = await supabase
        .from('project_tasks')
        .delete()
        .in('id', taskIds);

      if (error) throw error;

      fetchProjectTasks();
      setSelectedTasks([]);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error deleting tasks:', error);
      }
    }
  };

  const handleExportProject = async () => {
    // This would be implemented with a proper PDF generation service
    alert('Export functionality will be implemented with PDF generation service');
  };

  const handleCloneProject = async () => {
    if (!supabase || !project) return;

    try {
      // Create new project
      const { data: newProject, error: projectError } = await supabase
        .from('projects')
        .insert({
          name: `${project.name} (Copy)`,
          description: project.description,
          status: 'active',
          client_id: project.client_id,
          created_by: userProfile?.id
        })
        .select()
        .single();

      if (projectError) throw projectError;

      // Clone tasks
      const newTasks = tasks.map(task => ({
        ...task,
        id: undefined,
        project_id: newProject.id,
        created_at: undefined,
        updated_at: undefined
      }));

      const { error: tasksError } = await supabase
        .from('project_tasks')
        .insert(newTasks);

      if (tasksError) throw tasksError;

      navigate(`/portal/projects/${newProject.id}`);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error cloning project:', error);
      }
    }
  };

  const handleArchiveProject = async () => {
    if (!supabase || !project) return;
    if (!window.confirm('Are you sure you want to archive this project?')) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({
          status: 'completed',
          end_date: new Date().toISOString()
        })
        .eq('id', project.id);

      if (error) throw error;

      navigate('/portal/projects');
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error archiving project:', error);
      }
    }
  };

  const calculateProgress = () => {
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(t => t.status === 'completed').length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const selectedTask = tasks.find(t => t.id === selectedTaskId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Project not found</p>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-full flex flex-col" style={{ backgroundColor: colors.premium.darker }}>
        {/* Header */}
        <div className="flex-shrink-0 backdrop-blur-sm border-b border-white/10 px-6 py-4" style={{ backgroundColor: colors.premium.dark }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/portal/projects')}
                className="mr-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-400 hover:text-white" />
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-white">{project.name}</h1>
                    {project.client && (
                      <p className="text-sm text-gray-400">
                        Client: {project.client.name}
                      </p>
                    )}
                  </div>
                  {/* Progress indicator */}
                  <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                    <div className="text-sm text-gray-400">Progress:</div>
                    <div className="w-32 bg-white/10 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all duration-500 shadow-lg"
                        style={{
                          width: `${calculateProgress()}%`,
                          background: `linear-gradient(90deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                          boxShadow: `0 2px 8px -2px ${colors.brand.primary}60`
                        }}
                      />
                    </div>
                    <div className="text-sm font-medium text-white">{calculateProgress()}%</div>
                  </div>
                  {/* Active users */}
                  {activeUsers.length > 0 && (
                    <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                      <UsersIcon className="h-4 w-4 text-gray-400" />
                      <div className="flex -space-x-1">
                        {activeUsers.slice(0, 3).map(user => (
                          <div
                            key={user.id}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-medium ring-2 shadow-lg"
                            style={{
                              backgroundColor: colors.brand.primary,
                              borderColor: colors.premium.dark
                            }}
                            title={user.full_name}
                          >
                            {user.full_name?.split(' ').map(n => n[0]).join('')}
                          </div>
                        ))}
                        {activeUsers.length > 3 && (
                          <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-700 text-gray-300 text-xs font-medium ring-2" style={{ borderColor: colors.premium.dark }}>
                            +{activeUsers.length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* View toggle */}
              <div className="flex bg-white/5 backdrop-blur-sm rounded-lg p-1 border border-white/10">
                <button
                  onClick={() => setDisplayMode('visual')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${displayMode === 'visual'
                    ? 'text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  style={{
                    background: displayMode === 'visual' ? `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` : undefined
                  }}
                >
                  <ViewColumnsIcon className="h-4 w-4 inline mr-1" />
                  Visual
                </button>
                <button
                  onClick={() => setDisplayMode('list')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${displayMode === 'list'
                    ? 'text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  style={{
                    background: displayMode === 'list' ? `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` : undefined
                  }}
                >
                  <TableCellsIcon className="h-4 w-4 inline mr-1" />
                  List
                </button>
              </div>

              {displayMode === 'visual' && (
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as any)}
                  className="rounded-lg bg-white/10 border-white/10 text-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 sm:text-sm backdrop-blur-sm"
                >
                  <option value="vertical" className="bg-gray-800">Vertical View</option>
                  <option value="flowchart" className="bg-gray-800">Flowchart View</option>
                  <option value="timeline" className="bg-gray-800">Timeline View</option>
                  <option value="kanban" className="bg-gray-800">Kanban View</option>
                </select>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-1 border-l border-white/10 pl-2">
                <button
                  onClick={handleCreateTask}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-lg text-white transition-all duration-200 hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                    boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                  }}
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Add Task
                </button>

                {/* More actions dropdown */}
                <div className="relative inline-block text-left">
                  <button
                    className="inline-flex items-center p-2 border border-white/10 rounded-lg text-gray-300 bg-white/5 hover:bg-white/10 hover:text-white backdrop-blur-sm transition-all duration-200"
                    onClick={(e) => {
                      const menu = e.currentTarget.nextElementSibling;
                      if (menu) {
                        menu.classList.toggle('hidden');
                      }
                    }}
                  >
                    <Cog6ToothIcon className="h-5 w-5" />
                  </button>
                  <div className="hidden origin-top-right absolute right-0 mt-2 w-56 rounded-lg shadow-2xl border border-white/10 z-50" style={{ backgroundColor: colors.premium.dark }}>
                    <div className="py-1">
                      <button
                        onClick={() => setShowProjectSettings(true)}
                        className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white w-full text-left transition-colors"
                      >
                        <Cog6ToothIcon className="mr-3 h-5 w-5 text-gray-500" />
                        Project Settings
                      </button>
                      <button
                        onClick={handleExportProject}
                        className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white w-full text-left transition-colors"
                      >
                        <DocumentArrowDownIcon className="mr-3 h-5 w-5 text-gray-500" />
                        Export as PDF
                      </button>
                      <button
                        onClick={handleCloneProject}
                        className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white w-full text-left transition-colors"
                      >
                        <DocumentDuplicateIcon className="mr-3 h-5 w-5 text-gray-500" />
                        Clone Project
                      </button>
                      <hr className="my-1 border-white/10" />
                      <button
                        onClick={handleArchiveProject}
                        className="flex items-center px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 w-full text-left transition-colors"
                      >
                        <ArchiveBoxIcon className="mr-3 h-5 w-5 text-red-500" />
                        Archive Project
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {displayMode === 'visual' ? (
            <>
              {/* Visual Journey Canvas */}
              <div className="flex-1" style={{ backgroundColor: colors.premium.darker }}>
                <VisualJourneyCanvas
                  projectId={project.id}
                  tasks={filteredTasks}
                  selectedTaskId={selectedTaskId}
                  onTaskSelect={handleTaskSelect}
                  onTaskStatusChange={handleTaskStatusChange}
                  viewMode={viewMode}
                />
              </div>

              {/* Task Side Panel */}
              {selectedTask && (
                <TaskSidePanel
                  task={selectedTask}
                  isOpen={!!selectedTaskId}
                  onClose={() => setSelectedTaskId(null)}
                  projectId={project.id}
                  onEdit={() => handleEditTask(selectedTask)}
                  onDelete={() => handleDeleteTask(selectedTask.id)}
                />
              )}
            </>
          ) : (
            /* List View */
            <div className="flex-1" style={{ backgroundColor: colors.premium.darker }}>
              <TaskListView
                tasks={filteredTasks}
                selectedTasks={selectedTasks}
                onSelectTask={(taskId) => {
                  if (selectedTasks.includes(taskId)) {
                    setSelectedTasks(selectedTasks.filter(id => id !== taskId));
                  } else {
                    setSelectedTasks([...selectedTasks, taskId]);
                  }
                }}
                onSelectAll={(selected) => {
                  setSelectedTasks(selected ? filteredTasks.map(t => t.id) : []);
                }}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                onStatusChange={handleTaskStatusChange}
                onReorderTasks={handleReorderTasks}
                onBulkStatusUpdate={handleBulkStatusUpdate}
                onBulkDelete={handleBulkDelete}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filterStatus={filterStatus}
                onFilterChange={setFilterStatus}
              />
            </div>
          )}
        </div>

        {/* Modals */}
        {showTaskModal && (
          <TaskModal
            isOpen={showTaskModal}
            onClose={() => setShowTaskModal(false)}
            onSave={handleSaveTask}
            task={editingTask}
            projectId={projectId!}
            existingTasks={tasks}
          />
        )}

        {showProjectSettings && (
          <ProjectSettingsModal
            isOpen={showProjectSettings}
            onClose={() => setShowProjectSettings(false)}
            project={project}
            onSave={async (updatedProject) => {
              if (!supabase) return;
              try {
                const { error } = await supabase
                  .from('projects')
                  .update(updatedProject)
                  .eq('id', project.id);

                if (error) throw error;

                setProject({ ...project, ...updatedProject });
                setShowProjectSettings(false);
              } catch (error) {
                if (import.meta.env.DEV) {
                  console.error('Error updating project:', error);
                }
              }
            }}
          />
        )}
      </div>
    </DndProvider>
  );
};